// formDependencies.js
import { reactive, watch, nextTick } from 'vue'

// Global form state
export const formState = reactive({
  dependents: {},
  visibilityDependents: {},
  selectedValues: {},
  savedValues: {},
  selectOptions: {},
  loading: false
})

// Helper functions
const findDependentElements = (pattern) => {
  let regex
  if (pattern.startsWith('/') && pattern.endsWith('/')) {
    regex = new RegExp(pattern.slice(1, -1)) // Extract regex pattern without delimiters
  } else {
    regex = new RegExp(`^${pattern}$`) // Treat as exact match
  }
  
  return Array.from(document.querySelectorAll('[id]'))
    .filter(el => regex.test(el.id))
    .map(el => el.id)
}

const parseJSON = (str, fallback) => {
  try {
    return JSON.parse(str)
  } catch (e) {
    console.error('JSON parsing error:', e)
    return fallback
  }
}

const getElementValue = (el) => {
  if (el.matches('[data-pc-name="datepicker"] > input[type="text"]')) {
    return el.value
  } else if (el.matches('select')) {
    return el.value
  } else if (el.matches('.multiselect')) {
    const hiddenSelect = el.querySelector('select[multiple]')
    if (hiddenSelect) {
      return Array.from(hiddenSelect.selectedOptions).map(opt => opt.value)
    }
  }
  return el.value || []
}

const setElementValue = (el, value) => {
  if (!el) return;

  if (el.matches('[data-pc-name="datepicker"] > input[type="text"]')) {
    el.dispatchEvent(new CustomEvent('update:modelValue', {
      detail: value,
      bubbles: true
    }))
  } else if (el.matches('.multiselect')) {
    const filteredValue = (Array.isArray(value) ? value : [value]).filter(v => v !== '')

    el.dispatchEvent(new CustomEvent('update:modelValue', {
      detail: filteredValue,
      bubbles: true
    }))
  } else {
    el.value = value
  }
}

const addOptionToSelect = (sel, option) => {
  const opt = document.createElement('option')

  if (Array.isArray(option)) {
    opt.value = option[0]
    opt.textContent = option[1]
  } else {
    opt.value = option.value
    opt.textContent = option.text
  }

  sel.appendChild(opt)
}

// Options should be in [ { text: text, value: value }, ... ] format
const setElementOptions = (el, options) => {
  if (el.matches('select')) {
    el.innerHTML = ''
    addOptionToSelect(el, { text: '', value: '' })
    options.forEach(opt => { addOptionToSelect(el, opt) })
  } else if (el.matches('.multiselect')) {
    // For Multiselect component, emit options update event

    const optionsHash = options.map(([value, text]) => ({
      text: text,
      value: value
    }))

    el.dispatchEvent(new CustomEvent('setOptions', {
      detail: optionsHash,
      bubbles: true
    }))
  }
}

// Dependents is a hash
const setDependentIdsOfElement = (el, dependents) => {
  const elementId = el.id
  const dependentsString = el.getAttribute('data-dependents')

  if (dependentsString) {
    const regexPatterns = dependentsString.includes('[')
      ? parseJSON(dependentsString, [])
      : dependentsString.split(',').map(p => p.trim())

    const matchedDependents = regexPatterns.reduce((acc, pattern) => {
      const matches = findDependentElements(pattern)
      return [...acc, ...matches]
    }, [])

    // Make sure it can't depend on itself
    dependents[elementId] = [...new Set(matchedDependents)].filter(id => id !== elementId)
  }
}

// Parse visibility dependents
const setVisibilityDependentIdsOfElement = (el, visibilityDependents) => {
  const elementId = el.id

  const visibilityDependentsString = el.getAttribute('data-visibility-dependents')
  if (visibilityDependentsString) {
    visibilityDependents[elementId] = parseJSON(visibilityDependentsString, {})
  }
}

// Main plugin
export const formDependenciesPlugin = {
  install: (app, options = {}) => {
    const API_ENDPOINT = '/admin/select_options'

    // Need to save the data on each dependent ID; if the independent select changes,
    // if the other fields update and they maintain some old values, it should not clear
    // those dependent selects
    const saveDependentValues = (independentId) => {
      const dependentIds = formState.dependents[independentId] || []

      dependentIds.forEach(dependentId => {
        const dependentElement = document.getElementById(dependentId)

        if (dependentElement) {
          formState.savedValues[dependentId] = formState.selectedValues[dependentId]
        }
      })
    }

    // Assume independentElement exists without error
    const setSelectedValues = (independentId, independentValue) => {
      formState.selectedValues[independentId] = independentValue

      const dependentIds = formState.dependents[independentId] || []

      dependentIds.forEach(dependentId => {
        const dependentElement = document.getElementById(dependentId)

        if (dependentElement) {
          formState.selectedValues[dependentId] = getElementValue(dependentElement)
        }
      })
    }

    const setupValueChangeListeners = (el) => {
      // console.log("Setting up change listeners for: ", el)

      // This is for when the user selects and changes the multiselect
      if (!el._selectChangeHandler) {
        el._selectChangeHandler = function(el, event) {
          // console.log("select handler triggered")
          handleElementValueChange(el, getElementValue(el))
        }.bind(null, el)
        el.addEventListener('change', el._selectChangeHandler)
      }

      // This is for when externally, a custom event is sent to the multiselect;
      // We want that event to propagate through here so that everything stays consistent
      if (!el._multiselectChangeHandler) {
        el._multiselectChangeHandler = function(el, event) {
          // console.log("multiselect handler triggered")
          handleElementValueChange(el, event.detail)
        }.bind(null, el)
        el.addEventListener('update:modelValue', el._multiselectChangeHandler)
      }
    }

    const handleElementValueChange = async (el, value) => {
      const elementId = el.id

      // console.log("Element changed", elementId)
      formState.selectedValues[elementId] = value
      if (formState.dependents[elementId]) {
        await refreshDependents([elementId])
      }

      // console.log("Updating visibility of dependents")
      updateVisibilityOfDependents(elementId, value)
    }

    // inputsHash will be passed to the remote API call
    const getAllIndependentInputs = () => {
      return Object.keys(formState.dependents).reduce((inputsHash, independentId) => {
        inputsHash[independentId] = formState.selectedValues[independentId]
        return inputsHash
      }, {})
    }

    const refreshDependents = async (changedElementIds = Object.keys(formState.dependents)) => {
      formState.loading = true

      // console.log('FD: Refreshing dependents for ', changedElementIds)

      try {
        const allInputs = getAllIndependentInputs()

        const dependentIds = changedElementIds.reduce((acc, independentId) => {
          return formState.dependents[independentId]
            ? [...acc, ...formState.dependents[independentId]]
            : acc
        }, new Set())

        if (dependentIds.length > 0) {
          const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
          const response = await fetch(API_ENDPOINT, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken
            },
            body: JSON.stringify({
              inputs: allInputs,
              output_select_ids: dependentIds,
              dependency_tree: formState.dependents
            })
          })

          const dependentOptions = await response.json()
          // console.log('FD: refreshing dependents: ', dependentOptions)
          // Outputs a hash of the form:
          // {
          //   dependent1_id: { value1: text1, value2: text2, value3: text3, ... },
          //   dependent2_id: { ... }
          // }

          // Save existing values before refreshing the options
          changedElementIds.forEach(independentId => {
            // console.log('Saving dependent values of independent control: ', independentId)
            saveDependentValues(independentId)
          })

          // Update options for each dependent
          // console.log("dependentOptions", dependentOptions);
          Object.entries(dependentOptions).forEach(([dependentId, options]) => {
            formState.selectOptions[dependentId] = options
            const dependentElement = document.getElementById(dependentId)
            if (dependentElement) { setElementOptions(dependentElement, options) }
          })

          // Restore valid previous values
          // console.log("Restoring values for dependents of", changedElementIds);
          changedElementIds.forEach(independentId => {
            Object.entries(formState.savedValues).forEach(([dependentId, value]) => {
              // console.log("Restoring values for dependent", dependentId, ' with values ', value);

              if (!dependentOptions[dependentId]) {
                return
              }

              const dependentElement = document.getElementById(dependentId)

              if (Array.isArray(value)) {
                const validValues = value.filter(val => dependentOptions[dependentId]?.some(option => option[0]?.toString() === val?.toString()))

                if (validValues.length > 0) {
                  formState.selectedValues[dependentId] = validValues
                  setElementValue(dependentElement, validValues)
                } else {
                  formState.selectedValues[dependentId] = []
                  setElementValue(dependentElement, [])
                }
              } else if (dependentOptions[dependentId]?.some(option => option[0]?.toString() === value?.toString())) { // option[0] is the first
                // We treat '37' and 37 to be the same
                formState.selectedValues[dependentId] = value
                const dependentElement = document.getElementById(dependentId)
                setElementValue(dependentElement, value)
              }
            })
          })
        }

        // console.log('FD: dependent refresh complete')
      } catch (error) {
        console.error('Error refreshing dependents:', error)
      } finally {
        formState.loading = false
      }
    }

    const updateVisibilityOfDependents = (independentId, independentValue) => {
      if (!formState.visibilityDependents[independentId]) return

      formState.visibilityDependents[independentId].forEach(dependentHash => {
        Object.entries(dependentHash).forEach(([dependentId, independentValuesThatMakeDependentElementVisible]) => {
          let dependentVisible = false

          if (independentValuesThatMakeDependentElementVisible === true) {
            dependentVisible = true
          } else {
            const independentValuesThatMakeDependentElementVisibleArray = [].concat(independentValuesThatMakeDependentElementVisible)

            dependentVisible = Array.isArray(independentValue)
              ? independentValue.some(v => independentValuesThatMakeDependentElementVisibleArray.includes(v))
              : independentValuesThatMakeDependentElementVisibleArray.includes(independentValue)
          }

          const dependentElement = document.getElementById(dependentId)

          if (dependentElement) {
            const formRow = dependentElement.closest('.form-row')
            if (formRow) {
              formRow.classList.toggle('!hidden', !dependentVisible)
              // formRow.style.display = dependentVisible ? 'block' : 'none' // This may need to be generalized
            }
          }
        })
      })
    }

    // Function to initialize current values and refresh dependents
    const initializeFormState = async () => {
      // console.log('FD: initializing form state')
      if (formState.loading) return

      // Initialize current values for all elements with dependencies
      const independentIds = Object.keys(formState.dependents)
      independentIds.forEach(independentId => {
        const independentElement = document.getElementById(independentId)
        if (independentElement) {
          const independentValue = getElementValue(independentElement)
          setSelectedValues(independentId, independentValue)
        }
      })

      const visibilityIndependentIds = Object.keys(formState.visibilityDependents)
      visibilityIndependentIds.forEach(visibilityIndependentId => {
        const visibilityIndependentElement = document.getElementById(visibilityIndependentId)
        if (visibilityIndependentElement) {
          const visibilityIndependentValue = getElementValue(visibilityIndependentElement)
          updateVisibilityOfDependents(visibilityIndependentId, visibilityIndependentValue)
        }
      })

      // console.log('FD: selectedValues', formState.selectedValues)

      // console.log('FD: independent fields updated')
      // Refresh all dependents with current values
      await refreshDependents()
    }

    // Vue directive
    // el can be both independent or dependent ones
    app.directive('dependent-control', {
      mounted(el) {
        setDependentIdsOfElement(el, formState.dependents)
        setVisibilityDependentIdsOfElement(el, formState.visibilityDependents)
        setupValueChangeListeners(el)

        formState.dependents[el.id]?.forEach(dependentId => {
          const dependentElement = document.getElementById(dependentId)
          if (dependentElement) {
            setupValueChangeListeners(dependentElement)
          }
        })

        // Initialize form state once after all elements are set up
        if (!formState.loading) {
          nextTick(() => {
            if (!formState.loading) {
              initializeFormState()
            }
          })
        }
      },
      unmounted(el) {
        if (el._selectChangeHandler) {
          el.removeEventListener('change', el._selectChangeHandler)
        }

        if (el._multiselectChangeHandler) {
          el.removeEventListener('update:modelValue', el._multiselectChangeHandler)
        }
      }
    })

    // Expose initialization function for manual triggering if needed
    app.config.globalProperties.$initializeFormDependencies = initializeFormState
  }
}

export const initializeFormDependencies = async () => {
  return initializeFormState()
}
